/** Naming and Markdown for images kept beside a document. Pure, so it can be tested. */

/** Pictures are shrunk when they are added, so a large original is fine. */
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
/** The widest a kept picture is; wider ones are scaled down, never enlarged. */
export const MAX_IMAGE_WIDTH = 1600;
/** Types that are kept exactly as they are: animations and drawings must not be flattened. */
export const KEPT_AS_IS = new Set(["image/gif", "image/svg+xml"]);

const TYPES: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/svg+xml": "svg",
};

/** The file extension for an image type, or null when Quire does not keep that kind. */
export const imageExtension = (mime: string): string | null => TYPES[mime.toLowerCase()] ?? null;

/** The image type from a file name, for files the browser gave no type. */
export function typeFromName(fileName: string): string {
  const ext = /\.([^.]+)$/.exec(fileName)?.[1]?.toLowerCase() ?? "";
  const found = Object.entries(TYPES).find(([, e]) => e === ext || (ext === "jpeg" && e === "jpg"));
  return found?.[0] ?? "";
}

/** The words in a file name, as a description: "My-trip_photo.JPG" becomes "My trip photo". */
export function describeImage(fileName: string): string {
  const stem = fileName.replace(/\.[^.]*$/, "");
  const words = stem
    .replace(/[-_]+/g, " ")
    .replace(/[[\]()]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return words || "image";
}

/**
 * The name of a kept picture: the note's slug, the original name and a short
 * fingerprint of the original, such as images/my-trip-beach-3fa9c2d1.webp. The same
 * picture added twice gets the same name, so it is kept once.
 */
export function imagePath(slug: string, fileName: string, hash: string, ext: string): string {
  const clean = (text: string, max: number) =>
    text
      .normalize("NFD")
      .replace(/\p{M}+/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, max)
      .replace(/-+$/g, "");
  const stem = clean(fileName.replace(/\.[^.]*$/, ""), 40);
  const parts = [clean(slug, 40), stem, hash.slice(0, 8)].filter(Boolean);
  // A pasted screenshot has no useful name of its own.
  return `images/${parts.join("-")}.${ext}`;
}

/** The note's slug: its file name without the extension. */
export const slugOfPath = (path: string): string => path.replace(/\.(md|markdown)$/i, "");

/** Every kept picture a piece of Markdown points at, as a set of paths. */
export function imagesIn(text: string): Set<string> {
  const found = new Set<string>();
  for (const m of text.matchAll(/!\[[^\]]*\]\((images\/[^)\s]+)/g)) found.add(m[1]!);
  for (const m of text.matchAll(/<img[^>]*\ssrc=["'](images\/[^"']+)["']/gi)) found.add(m[1]!);
  return found;
}

/** The pictures kept but not used by any of these notes. */
export function unusedImages(kept: string[], notes: string[]): string[] {
  const used = new Set<string>();
  for (const note of notes) for (const path of imagesIn(note)) used.add(path);
  return kept.filter((path) => !used.has(path));
}

/** ![description](images/name.webp), with the caption as the picture's title when given. */
export function imageMarkdown(alt: string, path: string, caption = ""): string {
  const title = caption.trim().replace(/"/g, "'");
  return title ? `![${alt}](${path} "${title}")` : `![${alt}](${path})`;
}

/** True for an address that points at a file kept by Quire. */
export const isKeptImage = (src: string | null): src is string =>
  src !== null && /^images\/[^/\\:]+$/.test(src);
